<template>
  <div class="fixed top-0 h-full flex z-30 transition-all" :class="{'left-0 right-auto md:left-64 opacity-100' : isVisible, '-left-full md:-left-full opacity-0' : !isVisible}">
    <!-- Alert Panel -->
    <div class="relative flex flex-col w-96 bg-white rounded-lg shadow-lg m-2 p-6" :class="{
          'transition ease-in-out duration-300 transform': true,
          '-translate-x-full': !isVisible,
          'translate-x-0': isVisible,
        }">
      <div class="flex-1 flex flex-col gap-4 h-0">
        <div class="flex items-center">
          <h1 class="text-3xl text-bhav-cool-gray-900 leading-9 font-medium font-headline antialiased  flex-1">Alerts</h1>
          <nav class="flex items-center gap-1 flex-0">
            <router-link :to="{name: 'profile-tab', params: {tab: 'notification-settings'}}" class="flex items-center justify-center h-7 aspect-square rounded-full focus:outline-none bg-bhav-cool-gray-50 text-bhav-blue-500" aria-label="Read all" @click="closeAlert">
              <span class="fa fa-gear"></span>
            </router-link>
            <button class="flex items-center justify-center h-7 aspect-square rounded-full focus:outline-none bg-bhav-cool-gray-50 text-bhav-blue-500" aria-label="Close alerts" @click="closeAlert">
              <span class="fa fa-close"></span>
            </button>
          </nav>
        </div>
        <SearchInput v-model="search" placeholder="Search" />
        <!-- Alert list -->
        <div class="space-y-3 overflow-y-auto scrollbar-hide" v-if="filteredAlerts.length > 0" @scroll="handleScroll" ref="alertList">
          <AlertItem
              v-for="alert in filteredAlerts"
              :key="alert.id"
              :alert="alert"
              @close="closeAlert()"
          />
        </div>
        <div class="px-4 text-center text-bhav-blue-500 text-sm mt-8" v-else>
          <span class="far fa-bell text-2xl mb-2 block opacity-50"></span>
          No new alerts
        </div>
      </div>
      <div v-if="loading" class="text-center py-4 text-bhav-cool-gray-500 absolute inset-x-0 bottom-16 bg-white">
        <span class="fa fa-spinner fa-spin"></span>
      </div>
      <button type="button" @click="markAllRead" class="text-sm bg-bhav-blue-500 text-white rounded-lg text-center p-2 mt-2">Mark all as read</button>
    </div>
  </div>
</template>

<script>
import { AlertsController } from '@/controllers/alerts';
import AlertItem from './AlertItem.vue';
import Alert from '@/models/alert';
import SearchInput from './form/SearchInput.vue';
let timer;

export default {
  name: "AlertView",
  components: {
    AlertItem,
    SearchInput
  },
  emits: [
    'close',
    'ready'
  ],
  props: {
    isVisible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeFilter: 'unread',
      alerts: [],
      search: '',
      isFirstLoad: true,
      pagination: {
        start: 0,
        limit: 8,
        count: 0,
        unread: 0
      },
      loading: false
    }
  },
  computed: {
    filteredAlerts() {
      let results = this.alerts;

      if(this.search) {
        results = results
            .filter(result => {
              let alert = new Alert(result);
              return JSON.stringify({
                    id: alert.id,
                    created_at: alert.created_at,
                    date: alert.date,
                    since: alert.since,
                    headline: alert.headline,
                    diffs: alert.diffs,
                  }
              ).toLowerCase().includes(this.search.toLowerCase())
            });
      }

      return results;
    },
    hasMore() {
      return this.pagination.start + this.pagination.limit < this.pagination.count;
    }
  },
  mounted() {
    this.index(0);
    clearInterval(timer);
    timer = setInterval(() => this.index(0, true), (60 * 1000));
  },
  unmounted() {
    clearInterval(timer);
  },
  methods: {
    index(start = null, isRefreshing) {
      if (this.loading) return;
      // If start is provided, reset pagination and alerts list
      if (start !== null) {
        this.pagination.start = start;
      } else if(!isRefreshing) {
        // Check if there are more records to load
        if (!this.hasMore) return;
        this.pagination.start += this.pagination.limit;
      }

      this.loading = true;

      AlertsController.index(isRefreshing ? 0 : this.pagination.start, this.pagination.limit)
          .then(response => {
            // Filter out duplicates by checking existing alert IDs
            const existingIds = new Set(this.alerts.map(alert => alert.id));
            const newAlerts = response.data.filter(alert => !existingIds.has(alert.id)) ?? [];

            if (isRefreshing) {
              this.alerts = [...newAlerts, ...this.alerts];
              // send notifications
              this.sendNotifications(newAlerts);
            }
            else {
              this.alerts = [...this.alerts, ...newAlerts];
            }

            if (response.pagination) {
              if(isRefreshing){
                this.pagination.count = response.pagination.count;
                this.pagination.unread = response.pagination.unread;
              }
              else {
                this.pagination = response.pagination;
              }
            }
          })
          .catch(e => {
            throw e;
          })
          .finally(() => {
            this.loading = false;
          });
    },
    handleScroll(event) {
      const element = event.target;
      const scrollPosition = element.scrollTop + element.clientHeight;
      const scrollHeight = element.scrollHeight;

      // Trigger load when user scrolls to 95% of the container
      if (scrollPosition >= scrollHeight * 0.95 && this.hasMore && !this.loading) {
        this.index();
      }
    },
    sendNotifications(alerts) {
      if ('Notification' in window && Notification.permission === 'granted') {
        alerts.forEach(alert => {
          const alertModel = new Alert(alert);
          const notification = new Notification(alertModel.headline, {
            icon: alert.notificationIcon,
            tag: `alert-${alert.id}`,
            requireInteraction: false
          });

          notification.onclick = () => {
            window.focus();
            if (alertModel.url) {
              this.$router.push(alertModel.url);
            }
            notification.close();
          };
        });
      }
    },
    markAllRead() {
      AlertsController.bulkUpdate()
          .then(() => {
            this.alerts = this.alerts.map((alert) => {
              if(!alert.isRead) {
                alert.read_at = new Date();
              }
              return alert
            })
          })
          .catch(e => {
            throw e;
          });
    },
    closeAlert() {
      this.$emit('close');
    },
    clearSearch() {
      this.search = '';
    }
  },
  watch: {
    alerts: {
      handler(next,prev) {
        // FUTURE USE: awaiting addition of a control to enable/disable audio feedback in settings
        // if((next.find(alert => !alert.isRead)?.id ?? 0) > (prev.find(alert => !alert.isRead)?.id ?? 0)) {
        //   let audio = new Audio('https://cdn.freesound.org/previews/542/542042_6856600-lq.mp3');
        //   audio.play();
        // }
        this.$emit('ready', this.pagination.unread)
      },
      deep: true
    }
  }
}
</script>
